import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Diamond, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name || u.email}`);
      navigate(u.role === "admin" ? "/admin" : "/reseller", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-50">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-950">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/6745619/pexels-photo-6745619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-2">
            <Diamond className="w-6 h-6" strokeWidth={1.5} />
            <span className="font-display text-xl font-semibold tracking-tight">
              Shinning Eyes Entertainment
            </span>
          </div>
          <div className="space-y-4 max-w-md">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">
              Operator portal
            </p>
            <h1 className="font-display text-5xl font-semibold leading-tight">
              Allocate diamonds.<br />Process recharges.<br />Stay in control.
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed">
              The reseller &amp; admin console for Trumeet and Qwick Live recharges — session-aware proxy with full auditing.
            </p>
          </div>
          <div className="text-xs text-zinc-500 font-mono">SHINNING EYES ENTERTAINMENT PRIVATE LIMITED</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Sign in
            </p>
            <h2 className="font-display text-3xl font-semibold text-zinc-900">
              Welcome back
            </h2>
            <p className="text-sm text-zinc-500 mt-2">
              Enter your credentials to access the console.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-11"
              disabled={loading}
              data-testid="login-submit-button"
            >
              <Lock className="w-4 h-4 mr-2" strokeWidth={2} />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-zinc-500 hover:text-zinc-900 hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            <p className="text-xs text-zinc-500 leading-relaxed">
              Reseller accounts are created by an administrator. Contact your admin if you need access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
